<script>
  import { onMount } from 'svelte';
  import html2canvas from 'html2canvas';
  import Icon from '../Icon.svelte';
  import { APPS } from '../../os/apps.js';
  import { windows, focusWindow, launchApp } from '../../os/windows.svelte.js';
  import { spacesState, switchSpace, addSpace, removeSpace, reorderSpace, toggleMissionControl } from '../../os/spaces.svelte.js';

  let draggingSpaceId = $state(null);
  let dragOverSpaceId = $state(null);
  let thumbnails = $state({});

  async function captureThumbnails() {
    const results = {};
    for (const win of windows.filter(w => !w.minimized)) {
      const el = document.querySelector(`[data-window-id="${win.id}"]`);
      if (el) {
        try {
          const canvas = await html2canvas(el, {
            scale: 0.4,
            backgroundColor: null,
            allowTaint: true,
            useCORS: true,
            logging: false,
          });
          results[win.id] = canvas.toDataURL();
        } catch {
          results[win.id] = null;
        }
      }
    }
    thumbnails = results;
  }

  onMount(() => {
    requestAnimationFrame(() => captureThumbnails());
  });
</script>

<!-- svelte-ignore a11y_no_static_element_interactions: overlay click closes MissionControl; keyboard users can press Escape -->
<div class="mc-overlay" role="dialog" aria-label="Mission Control" tabindex="-1" onclick={() => toggleMissionControl()} onkeydown={(e) => { if (e.key === 'Escape') toggleMissionControl(); }}>
  <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
  <div class="mc-spaces" role="presentation" onclick={(e) => e.stopPropagation()}>
    {#each spacesState.spaces as space (space.id)}
      <div
        class="mc-space-thumb"
        class:mc-space-active={space.id === spacesState.activeSpaceId}
        class:mc-space-dragover={dragOverSpaceId === space.id}
        role="button" tabindex="0" draggable="true"
        ondragstart={(e) => { e.dataTransfer.effectAllowed = 'move'; draggingSpaceId = space.id; }}
        ondragenter={() => (dragOverSpaceId = space.id)}
        ondragover={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
        ondragleave={() => { if (dragOverSpaceId === space.id) dragOverSpaceId = null; }}
        ondrop={(e) => { e.preventDefault(); if (draggingSpaceId) reorderSpace(draggingSpaceId, space.id); dragOverSpaceId = null; draggingSpaceId = null; }}
        ondragend={() => { draggingSpaceId = null; dragOverSpaceId = null; }}
        onclick={() => switchSpace(space.id)}
        onkeydown={(event) => event.key === 'Enter' && switchSpace(space.id)}
      >
        <span class="mc-space-label">{space.name}</span>
        {#if spacesState.spaces.length > 1}
          <button class="mc-space-remove" aria-label="Remove desktop" onclick={(e) => { e.stopPropagation(); removeSpace(space.id); }}>×</button>
        {/if}
      </div>
    {/each}
    <button class="mc-add-space" onclick={addSpace} aria-label="Add Desktop">+</button>
  </div>

  <div class="mc-windows">
    {#each windows.filter(w => !w.minimized) as win (win.id)}
      <button
        class="mc-win-thumb"
        class:mc-win-active={win.spaceId === spacesState.activeSpaceId}
        onclick={(e) => { e.stopPropagation(); switchSpace(win.spaceId ?? 1); focusWindow(win.id); toggleMissionControl(); }}
      >
        <div class="mc-win-preview">
          <div class="mc-win-titlebar">
            <div class="mc-tl red"></div>
            <div class="mc-tl yellow"></div>
            <div class="mc-tl green"></div>
            <span class="mc-win-title">{win.title}</span>
          </div>
          <div class="mc-win-body">
            {#if thumbnails[win.id]}
              <img src={thumbnails[win.id]} alt={win.title} class="mc-win-shot" />
            {:else}
              <Icon name={APPS.find(a => a.id === win.appId)?.icon ?? 'document'} size={36} />
            {/if}
          </div>
        </div>
        <span class="mc-win-label">{win.title}</span>
      </button>
    {/each}
  </div>

  <p class="mc-hint">Click a window to switch · Ctrl+↑ to exit · Ctrl+← → to change space</p>
</div>

<style>
  .mc-overlay {
    position: fixed; inset: 0; z-index: 9999995;
    background: rgba(15, 17, 17, 0.84); backdrop-filter: blur(12px) saturate(.75);
    display: flex; flex-direction: column; align-items: center;
    padding: 20px 20px 80px; gap: 24px;
    animation: mc-in 0.3s cubic-bezier(0.22, 1, 0.36, 1);
  }
  @keyframes mc-in { from { opacity: 0; transform: scale(0.96); } to { opacity: 1; transform: scale(1); } }
  .mc-spaces { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; justify-content: center; }
  .mc-space-thumb {
    position: relative; min-width: 120px; height: 70px; padding: 6px 12px;
    background: rgba(255,255,255,0.08); border: 2px solid rgba(255,255,255,0.12); border-radius: 4px;
    color: #d0dcea; font: 600 12px var(--font); cursor: pointer;
    transition: background 0.15s, border-color 0.15s, transform 0.15s;
    display: flex; align-items: center; justify-content: center;
  }
  .mc-space-thumb:hover { background: rgba(255,255,255,0.14); transform: scale(1.04); }
  .mc-space-active { border-color: var(--graphite-focus) !important; background: linear-gradient(var(--graphite-select-top), var(--graphite-select-bot)) !important; }
  .mc-space-dragover { border-color: #e4e8e8 !important; box-shadow: 0 0 0 2px rgba(210,218,218,0.38) !important; transform: scale(1.05) !important; }
  .mc-space-remove {
    position: absolute; top: -8px; right: -8px; width: 18px; height: 18px; border-radius: 50%;
    background: rgba(60,60,60,0.9); color: #ccc; font-size: 13px; line-height: 1;
    display: flex; align-items: center; justify-content: center;
    border: 1px solid rgba(255,255,255,0.2); cursor: pointer; opacity: 0; transition: opacity 0.15s;
  }
  .mc-space-thumb:hover .mc-space-remove { opacity: 1; }
  .mc-space-label { pointer-events: none; }
  .mc-add-space {
    width: 44px; height: 44px; border-radius: 50%; background: rgba(255,255,255,0.1);
    border: 2px dashed rgba(255,255,255,0.25); color: rgba(255,255,255,0.6); font-size: 22px;
    line-height: 1; cursor: pointer; transition: background 0.15s, color 0.15s;
    display: flex; align-items: center; justify-content: center;
  }
  .mc-add-space:hover { background: rgba(255,255,255,0.18); color: #fff; }
  .mc-windows { display: flex; flex-wrap: wrap; gap: 16px; justify-content: center; align-items: flex-start; flex: 1; }
  .mc-win-thumb {
    display: flex; flex-direction: column; align-items: center; gap: 6px;
    background: transparent; border: 0; cursor: pointer;
    transition: transform 0.18s cubic-bezier(0.22, 1, 0.36, 1);
  }
  .mc-win-thumb:hover { transform: scale(1.07); }
  .mc-win-preview {
    width: 180px; height: 120px; border-radius: 3px; overflow: hidden;
    background: #1a2030; border: 2px solid rgba(255,255,255,0.1);
    box-shadow: 0 8px 24px rgba(0,0,0,0.5); display: flex; flex-direction: column;
  }
  .mc-win-active .mc-win-preview { border-color: var(--graphite-focus); }
  .mc-win-titlebar {
    height: 20px; background: linear-gradient(to bottom, #7a8290 0%, #5c6470 100%);
    display: flex; align-items: center; gap: 5px; padding: 0 7px; flex-shrink: 0;
  }
  .mc-tl { width: 8px; height: 8px; border-radius: 50%; }
  .mc-tl.red    { background: #ff5f57; }
  .mc-tl.yellow { background: #febc2e; }
  .mc-tl.green  { background: #28c840; }
  .mc-win-title {
    font: 600 10px var(--font); color: #e0eaf4; flex: 1; text-align: center;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
  .mc-win-body { flex: 1; display: flex; align-items: center; justify-content: center; overflow: hidden; }
  .mc-win-body :global(.os-icon) { opacity: 0.6; }
  .mc-win-shot {
    width: 100%; height: 100%; object-fit: cover;
    image-rendering: auto;
  }
  .mc-win-label {
    font: 11px var(--font); color: rgba(255,255,255,0.7);
    max-width: 180px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
  .mc-hint { font: 11px var(--font); color: rgba(255,255,255,0.35); text-align: center; margin: 0; }
</style>
